import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Users, Clock, Mail, AlertTriangle, Target,
  Calendar, BarChart3, PieChart as PieChartIcon, Activity, Loader2, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { autoClientesAPI } from '../api';

const COLORES = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#6b7280'];

function KpiCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-2.5 rounded-lg" style={{ backgroundColor: color ? `${color}15` : '#f3f4f6' }}>
          <Icon className="h-5 w-5" style={{ color: color || '#6b7280' }} />
        </div>
      </div>
    </CardContent></Card>
  );
}

function formatMinutes(mins) {
  if (!mins || mins === 0) return '0 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function EstadisticasClientes() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [remitentes, setRemitentes] = useState([]);
  const [volumen, setVolumen] = useState([]);
  const [clasificacion, setClasificacion] = useState([]);
  const [tiposConsulta, setTiposConsulta] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [horasPunta, setHorasPunta] = useState([]);
  const [periodoVolumen, setPeriodoVolumen] = useState('mes');
  const [periodoUsuario, setPeriodoUsuario] = useState('mes');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kR, rR, vR, cR, tR, uR, hR] = await Promise.all([
        autoClientesAPI.getEstadisticasKpis(),
        autoClientesAPI.getEstadisticasRemitente(),
        autoClientesAPI.getEstadisticasVolumen(periodoVolumen),
        autoClientesAPI.getEstadisticasClasificacion(),
        autoClientesAPI.getEstadisticasTipoConsulta(),
        autoClientesAPI.getEstadisticasUsuario(periodoUsuario),
        autoClientesAPI.getEstadisticasHorasPunta(),
      ]);
      setKpis(kR.data); setRemitentes(rR.data.remitentes || []); setVolumen(vR.data.datos || []);
      setClasificacion(cR.data.tipos || []); setTiposConsulta(tR.data.tipos || []);
      setUsuarios(uR.data.usuarios || []); setHorasPunta(hR.data.horas_punta || []);
    } catch (err) { console.error('Error estadisticas clientes:', err); }
    finally { setLoading(false); }
  }, [periodoVolumen, periodoUsuario]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePeriodoVolumen = async (p) => {
    setPeriodoVolumen(p);
    try { const r = await autoClientesAPI.getEstadisticasVolumen(p); setVolumen(r.data.datos || []); } catch {}
  };
  const handlePeriodoUsuario = async (p) => {
    setPeriodoUsuario(p);
    try { const r = await autoClientesAPI.getEstadisticasUsuario(p); setUsuarios(r.data.usuarios || []); } catch {}
  };

  const heatmapGrid = [];
  const dias = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
  const diaMap = { 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5, 1: 6 };
  const maxHp = Math.max(1, ...horasPunta.map(h => h.total));
  for (let d = 0; d < 7; d++) {
    for (let h = 7; h <= 20; h++) {
      const match = horasPunta.find(hp => diaMap[hp.dia_num] === d && hp.hora === h);
      heatmapGrid.push({ dia: d, hora: h, total: match ? match.total : 0 });
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      <span className="ml-3 text-gray-500">Cargando estadisticas...</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="estadisticas-clientes-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard title="Total emails" value={kpis?.total_emails || 0} icon={Mail} color="#3b82f6" />
        <KpiCard title="Tramitados" value={kpis?.tramitados || 0} icon={TrendingUp} color="#10b981" />
        <KpiCard title="Pendientes" value={kpis?.pendientes || 0} icon={Clock} color="#f59e0b" />
        <KpiCard title="Urgentes" value={kpis?.urgentes_pendientes || 0} icon={AlertTriangle} color="#ef4444" />
        <KpiCard title="Respondidos" value={kpis?.respondidos || 0} icon={MessageSquare} color="#14b8a6" />
        <KpiCard title="Tasa tramit." value={`${kpis?.tasa_tramitacion || 0}%`} icon={Target} color="#8b5cf6" />
        <KpiCard title="Tiempo medio" value={formatMinutes(kpis?.tiempo_medio_minutos)} icon={Clock} color="#6366f1" />
      </div>

      {/* Row 1: Volumen + Clasificacion IA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" /> Volumen de emails
              </CardTitle>
              <Select value={periodoVolumen} onValueChange={handlePeriodoVolumen}>
                <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoy">Hoy</SelectItem>
                  <SelectItem value="semana">Semana</SelectItem>
                  <SelectItem value="mes">Mes</SelectItem>
                  <SelectItem value="ano">Ano</SelectItem>
                  <SelectItem value="todo">Todo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {volumen.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={volumen} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="tramitados" name="Tramitados" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="pendientes" name="Pendientes" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-10 text-sm">Sin datos para este periodo</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChartIcon className="h-4 w-4 text-purple-600" /> Clasificacion IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clasificacion.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={clasificacion} dataKey="total" nameKey="label" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {clasificacion.map((t, i) => <Cell key={i} fill={t.color || COLORES[i % COLORES.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v} emails`, name]} contentStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {clasificacion.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color || COLORES[i] }} />
                        <span className="text-gray-600">{t.label}</span>
                      </div>
                      <span className="font-medium">{t.total}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-center text-gray-400 py-10 text-sm">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Remitentes + Tipo consulta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" /> Emails por remitente (Top 15)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {remitentes.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, Math.min(remitentes.length, 15) * 36)}>
                <BarChart data={remitentes.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="cliente" type="category" width={140} tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v.length > 22 ? v.substring(0, 20) + '...' : v} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="tramitados" name="Tramitados" fill="#10b981" stackId="a" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="pendientes" name="Pendientes" fill="#f59e0b" stackId="a" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 py-10 text-sm">Sin datos</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-pink-600" /> Tipo de consulta (IA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tiposConsulta.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={tiposConsulta} dataKey="total" nameKey="label" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {tiposConsulta.map((t, i) => <Cell key={i} fill={t.color || COLORES[i % COLORES.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v}`, name]} contentStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {tiposConsulta.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color || COLORES[i] }} />
                        <span className="text-gray-600">{t.label}</span>
                      </div>
                      <span className="font-medium">{t.total}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-center text-gray-400 py-10 text-sm">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Usuarios */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-green-600" /> Productividad por usuario
            </CardTitle>
            <Select value={periodoUsuario} onValueChange={handlePeriodoUsuario}>
              <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semana">Semana</SelectItem>
                <SelectItem value="mes">Mes</SelectItem>
                <SelectItem value="ano">Ano</SelectItem>
                <SelectItem value="todo">Todo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {usuarios.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={usuarios} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="usuario" tick={{ fontSize: 10 }} tickFormatter={(v) => v.length > 12 ? v.substring(0, 10) + '...' : v} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="total_tramitados" name="Tramitados" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="respondidos" name="Respondidos" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-medium text-gray-600">Usuario</th>
                      <th className="text-center px-2 py-1.5 font-medium text-gray-600">Tramit.</th>
                      <th className="text-center px-2 py-1.5 font-medium text-gray-600">Respond.</th>
                      <th className="text-center px-2 py-1.5 font-medium text-gray-600">T. medio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.map((u, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-1.5 font-medium">{u.usuario}</td>
                        <td className="text-center px-2 py-1.5">{u.total_tramitados}</td>
                        <td className="text-center px-2 py-1.5">{u.respondidos}</td>
                        <td className="text-center px-2 py-1.5 text-gray-500">{formatMinutes(u.tiempo_medio_min)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="text-center text-gray-400 py-10 text-sm">Sin datos para este periodo</p>}
        </CardContent>
      </Card>

      {/* Row 4: Horas punta */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-amber-600" /> Mapa de actividad - Emails recibidos por dia y hora
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="flex items-center gap-0.5 mb-1 ml-10">
                {Array.from({ length: 14 }, (_, i) => i + 7).map(h => (
                  <div key={h} className="flex-1 text-center text-[10px] text-gray-400">{h}h</div>
                ))}
              </div>
              {dias.map((dia, d) => (
                <div key={d} className="flex items-center gap-0.5 mb-0.5">
                  <div className="w-10 text-xs text-gray-500 text-right pr-2 font-medium">{dia}</div>
                  {Array.from({ length: 14 }, (_, i) => i + 7).map(h => {
                    const cell = heatmapGrid.find(c => c.dia === d && c.hora === h);
                    const val = cell ? cell.total : 0;
                    const intensity = val > 0 ? Math.min(0.15 + (val / maxHp) * 0.85, 1) : 0;
                    return (
                      <div key={h} className="flex-1 aspect-square rounded-sm flex items-center justify-center text-[9px] font-medium cursor-default transition-colors"
                        style={{ backgroundColor: val > 0 ? `rgba(20, 184, 166, ${intensity})` : '#f3f4f6', color: intensity > 0.5 ? '#fff' : '#6b7280', minHeight: '28px' }}
                        title={`${dia} ${h}:00 - ${val} emails`}>
                        {val > 0 ? val : ''}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
